import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { connectToSalesforce } from '../services/salesforceService';
import Navbar from '../components/Navbar';
import { supabase } from '../services/supabaseClient';

interface Org {
  id: string;
  name: string;
  instance_url: string;
  is_sandbox: boolean;
  last_synced_at: string | null;
}

interface SyncResults {
  processed: number;
  errors: number;
  details: Array<{
    flow: string;
    status: string;
    error?: string;
  }>;
}

export default function SalesforceFlowConnectorPage() {
  const { user, signOut } = useAuth();
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [environment, setEnvironment] = useState('production');
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [syncResults, setSyncResults] = useState<SyncResults | null>(null);
  const [isClearing, setIsClearing] = useState(false);
  const [clearResults, setClearResults] = useState<any | null>(null);
  const navigate = useNavigate();

  // Fetch orgs on component mount
  useEffect(() => {
    fetchOrgs();
  }, []);

  // Initialize environment from localStorage if available
  useEffect(() => {
    const savedEnv = localStorage.getItem('sf_env');
    if (savedEnv === 'sandbox' || savedEnv === 'production') {
      setEnvironment(savedEnv);
    }
  }, []);

  const fetchOrgs = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await supabase
        .from('sf_orgs')
        .select('*')
        .order('name');
      
      if (fetchError) {
        throw fetchError;
      }
      
      setOrgs(data || []);
      
      // If we have orgs, set the first one as selected by default
      if (data && data.length > 0) {
        setSelectedOrgId(data[0].id);
      }
    } catch (err: any) {
      console.error('Error fetching Salesforce orgs:', err);
      setError('Failed to load Salesforce organizations. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectSalesforce = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (!user) {
        setError('You must be logged in to connect a Salesforce org');
        setIsLoading(false);
        return;
      }
      
      // Ensure we have our consumer key
      const consumerKey = import.meta.env.VITE_SALESFORCE_CONSUMER_KEY;
      if (!consumerKey) {
        setError('Salesforce Consumer Key is not configured. Check your .env file.');
        setIsLoading(false);
        return;
      }
      
      // Store user authentication state and environment in localStorage before redirect
      localStorage.setItem('sf_auth_state', 'connecting');
      localStorage.setItem('sf_auth_user', user.id);
      localStorage.setItem('sf_env', environment);
      
      console.log(`Connecting to Salesforce ${environment} environment (sandbox=${environment === 'sandbox'})`);
      
      // Initiate Salesforce OAuth flow
      try {
        connectToSalesforce(environment);
        
        // If we're still here after 2 seconds, the redirect probably failed
        setTimeout(() => {
          if (document.hasFocus()) {
            setError('Redirect to Salesforce failed. Try the alternative direct link below.');
            setIsLoading(false);
          }
        }, 2000);
      } catch (redirectErr) {
        console.error('Error during redirect:', redirectErr);
        setError('Failed to redirect to Salesforce. Try the alternative link.');
        setIsLoading(false);
      }
    } catch (err: any) {
      console.error('Error initiating Salesforce connection:', err);
      setError('Failed to connect to Salesforce. Please try again.');
      setIsLoading(false);
    }
  };

  const handleSyncFlows = async () => {
    if (!selectedOrgId) {
      setError('Please select an organization to sync');
      return;
    }

    setIsSyncing(true);
    setSyncResults(null);
    setError(null);

    try {
      const response = await fetch('/api/sf_salesforce/sf_flow-sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orgId: selectedOrgId,
          incrementalSync: true
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to sync flows');
      }

      const data = await response.json();
      setSyncResults(data);

      // Refresh orgs to get updated last_synced_at timestamps
      fetchOrgs();
    } catch (err: any) {
      console.error('Error syncing flows:', err);
      setError(`Failed to sync flows: ${err.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleClearData = async () => {
    if (!selectedOrgId) {
      setError('Please select an organization');
      return;
    }

    // Confirm before proceeding
    const confirmed = window.confirm(
      'This will delete ALL flow data for the selected organization. This action cannot be undone. Are you sure you want to proceed?'
    );

    if (!confirmed) return;

    setIsClearing(true);
    setClearResults(null);
    setError(null);

    try {
      const response = await fetch('/api/sf_salesforce/sf_flow-clear-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orgId: selectedOrgId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to clear flow data');
      }

      const data = await response.json();
      setClearResults(data);

      // Refresh orgs to get updated last_synced_at timestamps
      fetchOrgs();
    } catch (err: any) {
      console.error('Error clearing flow data:', err);
      setError(`Failed to clear flow data: ${err.message}`);
    } finally {
      setIsClearing(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar onSignOut={handleSignOut} />
      
      <div className="py-10">
        <header>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl font-bold leading-tight text-gray-900">
              Salesforce Flow Connector
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Connect to Salesforce and sync Flow definitions for documentation
            </p>
          </div>
        </header>
        
        <main>
          <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <div className="px-4 py-8 sm:px-0">
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  {error && (
                    <div className="mb-4 rounded-md bg-red-50 p-4">
                      <div className="flex">
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">{error}</h3>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Connect to Salesforce Section */}
                  <div className="mb-8">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Connect to Salesforce</h2>
                    
                    {/* Environment selection */}
                    <div className="mb-5">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Salesforce Environment
                      </label>
                      <div className="flex space-x-4">
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            className="form-radio h-4 w-4 text-blue-600"
                            name="environment"
                            value="production"
                            checked={environment === 'production'}
                            onChange={() => setEnvironment('production')}
                          />
                          <span className="ml-2 text-sm text-gray-700">Production</span>
                        </label>
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            className="form-radio h-4 w-4 text-blue-600"
                            name="environment"
                            value="sandbox"
                            checked={environment === 'sandbox'}
                            onChange={() => setEnvironment('sandbox')}
                          />
                          <span className="ml-2 text-sm text-gray-700">Sandbox</span>
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {environment === 'production' 
                          ? 'Connect to your production Salesforce organization (login.salesforce.com)'
                          : 'Connect to your Salesforce sandbox environment (test.salesforce.com)'}
                      </p>
                    </div>
                    
                    <button
                      onClick={handleConnectSalesforce}
                      disabled={isLoading}
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                    >
                      {isLoading ? 'Connecting...' : `Connect to Salesforce ${environment === 'sandbox' ? 'Sandbox' : 'Production'}`}
                    </button>
                  </div>

                  {/* Connected Orgs Section */}
                  <div className="mb-8">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Connected Organizations</h2>
                    
                    {isLoading ? (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                      </div>
                    ) : orgs.length === 0 ? (
                      <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center text-gray-500">
                        No Salesforce organizations connected yet.
                      </div>
                    ) : (
                      <div>
                        <label htmlFor="org-select" className="block text-sm font-medium text-gray-700 mb-2">
                          Select an organization
                        </label>
                        <select
                          id="org-select"
                          value={selectedOrgId || ''}
                          onChange={(e) => setSelectedOrgId(e.target.value)}
                          className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                        >
                          {orgs.map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.name} ({org.is_sandbox ? 'Sandbox' : 'Production'})
                            </option>
                          ))}
                        </select>

                        {selectedOrgId && (
                          <div className="mt-3">
                            <p className="text-sm text-gray-500">
                              Last synced: {orgs.find(o => o.id === selectedOrgId)?.last_synced_at ? 
                                new Date(orgs.find(o => o.id === selectedOrgId)!.last_synced_at!).toLocaleString() : 
                                'Never'}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Sync Flows Section */}
                  {selectedOrgId && (
                    <div className="mb-8">
                      <h2 className="text-lg font-medium text-gray-900 mb-4">Flow Data Management</h2>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <button
                          onClick={handleSyncFlows}
                          disabled={isSyncing || isClearing || !selectedOrgId}
                          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-green-300"
                        >
                          {isSyncing ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Syncing Flows...
                            </>
                          ) : 'Sync Flows'}
                        </button>

                        <button
                          onClick={handleClearData}
                          disabled={isSyncing || isClearing || !selectedOrgId}
                          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-300"
                        >
                          {isClearing ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Clearing Data...
                            </>
                          ) : 'Clear Data'}
                        </button>
                      </div>

                      <p className="mt-2 text-xs text-gray-500">
                        Tip: Clear data before syncing to ensure consistent data population across all tables.
                      </p>
                    </div>
                  )}

                  {/* Results Section */}
                  {(syncResults || clearResults) && (
                    <div className="mt-6 bg-gray-50 p-4 rounded-md">
                      {/* Sync Results */}
                      {syncResults && (
                        <>
                          <h3 className="text-md font-medium text-gray-900 mb-2">Sync Results</h3>

                          <div className="mb-4">
                            <div className="grid grid-cols-3 gap-4">
                              <div className="col-span-1 bg-blue-50 p-3 rounded-md text-center">
                                <span className="block text-lg font-semibold">{syncResults.processed}</span>
                                <span className="text-sm text-gray-600">Processed</span>
                              </div>
                              <div className="col-span-1 bg-green-50 p-3 rounded-md text-center">
                                <span className="block text-lg font-semibold">{syncResults.processed - syncResults.errors}</span>
                                <span className="text-sm text-gray-600">Successful</span>
                              </div>
                              <div className="col-span-1 bg-red-50 p-3 rounded-md text-center">
                                <span className="block text-lg font-semibold">{syncResults.errors}</span>
                                <span className="text-sm text-gray-600">Errors</span>
                              </div>
                            </div>
                          </div>

                          {syncResults.details && syncResults.details.length > 0 && (
                            <div className="mt-4">
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Details</h4>
                              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Flow
                                      </th>
                                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {syncResults.details.map((detail, index) => (
                                      <tr key={index} className={detail.status === 'error' ? 'bg-red-50' : ''}>
                                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                                          {detail.flow}
                                        </td>
                                        <td className="px-6 py-2 whitespace-nowrap">
                                          {detail.status === 'success' ? (
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                              Success
                                            </span>
                                          ) : (
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                              Error
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* Clear Data Results */}
                      {clearResults && (
                        <>
                          <h3 className="text-md font-medium text-gray-900 mb-2">Data Clearing Results</h3>

                          <div className="mt-4">
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Records Deleted</h4>
                            <div className="border border-gray-200 rounded-md overflow-hidden">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Table
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Count
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Status
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {Object.entries(clearResults.counts || {}).map(([table, count], index) => (
                                    <tr key={index}>
                                      <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                                        {table}
                                      </td>
                                      <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                                        {count}
                                      </td>
                                      <td className="px-6 py-2 whitespace-nowrap">
                                        {clearResults.results[table] === 'Success' ? (
                                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                            Success
                                          </span>
                                        ) : (
                                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                            Error
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}